import React, { FC, Children, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { StaticImage } from 'gatsby-plugin-image';
import { A11y, SwiperOptions, Pagination, Navigation, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import { format } from 'date-fns';
import { Typography } from '../typography';
import Quote from '../quote/Quote';
import { Star } from '../icon';

// Interface

export interface FeefoCarouselProps {
  reviews?: {
    starCount?: number;
    title: string;
    reviewText: string;
    customerName: string;
    reviewDate: string;
  }[];
}
interface StyledSwiperProps {
  breakpoints?: SwiperOptions;
  $numberOfChildren?: number;
}

// Styles

const StyledFeefoCarousel = styled.div`
  ${({ theme: { media, space } }) => css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${media.medium}) {
      flex-direction: row;
      justify-content: space-between;
      gap: ${space.small};
    }
  `};
`;

const StyledImageWrapper = styled.div`
  ${() => css`
    flex: 1;
    justify-self: center;

    img {
      max-width: 100%;
    }
  `};
`;

const StyledNavigation = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    gap: ${space.xSmall};
    position: absolute;
    right: 0;

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `}
`;

const StyledSwiper = styled(Swiper)<StyledSwiperProps>`
  ${({ theme: { space, colors } }) => css`
    max-width: 100%;
    min-width: 1px;
    overflow: hidden;
    position: relative;
    flex: 2;
    display: flex !important;
    flex-direction: column;

    .swiper-slide {
      height: auto;
      max-width: 100%;
      margin-right: ${space.xSmall};

      .gatsby-image-wrapper-constrained {
        width: 100%;
      }
    }

    .swiper-pagination {
      position: relative;
      width: 100%;
      margin-top: ${space.xSmall};
    }

    .swiper-pagination-bullet.swiper-pagination-bullet-active {
      background-color: ${colors.brandTwo100};
    }
  `}
`;

const StyledSlide = styled.div`
  ${({ theme: { space } }) => css`
    padding: ${space.xxSmall};
    display: flex;
    flex-direction: column;
    font-size: 15px;
  `}
`;

const StyledSlideRow = styled.div`
  ${({ theme: { space } }) => css`
    text-align: center;
    margin-bottom: ${space.xxxSmall};
  `}
`;

const StyledSlideRowTitle = styled(StyledSlideRow)`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.xxSmall};
  `}
`;

const StyledStar = styled(Star)`
  ${({ theme: { colors } }) => css`
    fill: ${colors.neutralGold};
    display: inline-block;
    width: 20px;
  `}
`;

interface StarsProps {
  starCount?: number;
}

const Stars: React.FC<StarsProps> = ({ starCount = 5 }) => {
  const stars: React.ReactElement[] = [];

  for (let i = 0; i < starCount; i++) {
    stars.push(<StyledStar />);
  }

  return <>{stars}</>;
};

// JSX

export const FeefoCarousel: FC<FeefoCarouselProps> = ({ reviews }) => {
  const [controlledSwiper, setControlledSwiper] = useState<any>(null);
  const hasMount = useRef(false);
  const prevNav = useRef<HTMLButtonElement>(null);
  const nextNav = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (controlledSwiper && !hasMount.current) {
      controlledSwiper.init();
      hasMount.current = true;

      setTimeout(() => {
        controlledSwiper.update();
      }, 300);
    }
  }, [controlledSwiper]);
  const opts: SwiperOptions = {
    watchOverflow: true,
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: true,
    },
    pagination: {
      clickable: true,
      type: 'bullets',
    },
    navigation: {
      prevEl: prevNav.current,
      nextEl: nextNav.current,
    },
    preventInteractionOnTransition: true,
    spaceBetween: 16,

    slidesPerView: 1,
  };

  const slides = reviews?.map((review) => (
    <StyledSlide>
      <StyledSlideRow>
        <Stars starCount={review.starCount} />
      </StyledSlideRow>
      <StyledSlideRowTitle>
        <Quote text={review.title} />
      </StyledSlideRowTitle>
      <StyledSlideRow>{review.reviewText}</StyledSlideRow>
      <StyledSlideRow>
        {review.customerName} -{' '}
        {format(new Date(review?.reviewDate), 'EEEE, MMMM dd, yyyy')}
      </StyledSlideRow>
    </StyledSlide>
  ));

  return (
    <StyledFeefoCarousel>
      <StyledImageWrapper>
        <StaticImage src="./feefo-5-star.webp" alt="feefo 5 stars" />
      </StyledImageWrapper>

      <StyledSwiper
        onSwiper={setControlledSwiper}
        wrapperTag="div"
        modules={[Pagination, A11y, Navigation, Autoplay]}
        {...opts}
        breakpoints={opts?.breakpoints}
        $numberOfChildren={Children.count(slides)}
        className="carousel-swiper"
      >
        {React.Children.map(slides, (slide, i) => (
          <SwiperSlide key={`swiper-${i}`} tag="div">
            {React.cloneElement(slide as ReactElement, {})}
          </SwiperSlide>
        ))}

        <StyledNavigation />
      </StyledSwiper>

      <StyledImageWrapper>
        <StaticImage src="./feefo-platinum.webp" alt="feefo platinum" />
      </StyledImageWrapper>
    </StyledFeefoCarousel>
  );
};

export default FeefoCarousel;
